import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Expand, Filter, ChevronDown, LayoutGrid, Compass, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import plotData, { MEDIA } from '@/data/plotData';
import { LANDING } from '@/constants/testIds';

const statusColors = {
  available: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  launch: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  sold: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-500' },
};

const facingTranslations = {
  en: { EAST: 'East', WEST: 'West', NORTH: 'North', SOUTH: 'South', 'NORTH & EAST': 'NE', 'NORTH & WEST': 'NW', 'SOUTH & EAST': 'SE' },
  ta: { EAST: '\u0B95\u0BBF\u0BB4\u0B95\u0BCD\u0B95\u0BC1', WEST: '\u0BAE\u0BC7\u0BB1\u0BCD\u0B95\u0BC1', NORTH: '\u0BB5\u0B9F\u0B95\u0BCD\u0B95\u0BC1', SOUTH: '\u0BA4\u0BC6\u0BB1\u0BCD\u0B95\u0BC1', 'NORTH & EAST': '\u0BB5.\u0B95\u0BBF', 'NORTH & WEST': '\u0BB5.\u0BAE\u0BC7', 'SOUTH & EAST': '\u0BA4\u0BC6.\u0B95\u0BBF' },
};

export default function PlotLayout({ plots: plotsProp, onPlotClick, onVaastuClick }) {
  const { lang } = useLanguage();
  const t = translations[lang].plots;
  const tv = translations[lang].vaastu;
  const [filter, setFilter] = useState('all');
  const [showMap, setShowMap] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const allPlots = plotsProp || plotData;

  const filteredPlots = useMemo(() => {
    if (filter === 'all') return allPlots;
    if (filter === 'vaastu') return allPlots.filter(p => p.vaastu && p.status !== 'sold');
    return allPlots.filter(p => p.status === filter);
  }, [filter, allPlots]);

  const counts = useMemo(() => ({
    all: allPlots.length,
    available: allPlots.filter(p => p.status === 'available').length,
    launch: allPlots.filter(p => p.status === 'launch').length,
    sold: allPlots.filter(p => p.status === 'sold').length,
    vaastu: allPlots.filter(p => p.vaastu && p.status !== 'sold').length,
  }), [allPlots]);

  const filters = [
    { key: 'all', label: t.filterAll, count: counts.all },
    { key: 'available', label: t.filterAvailable, count: counts.available },
    { key: 'vaastu', label: lang === 'en' ? 'Vaastu Premium' : '\u0BB5\u0BBE\u0BB8\u0BCD\u0BA4\u0BC1', count: counts.vaastu },
    { key: 'launch', label: t.filterLaunch, count: counts.launch },
    { key: 'sold', label: t.filterSold, count: counts.sold },
  ];

  const statusLabel = (status) => {
    if (status === 'available') return t.statusAvailable;
    if (status === 'launch') return t.statusLaunch;
    return t.statusSold;
  };

  return (
    <section id="plots" data-testid={LANDING.plotLayout} className="py-16 sm:py-24 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12">
          <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">{t.sectionTag}</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2520] mt-3 tracking-tight">{t.title}</h2>
          <p className="text-[#5C6A61] text-base mt-3 max-w-2xl">{t.subtitle}</p>
          {/* Instruction text for interactivity */}
          <p className="text-[#D48C57] text-sm mt-4 font-medium flex items-center gap-2">
            <Eye className="w-4 h-4" />
            {lang === 'en'
              ? 'Tap any plot below to visualize your future home. See how a luxury modern villa seamlessly fits your dimensions.'
              : '\u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B8E\u0BA4\u0BBF\u0BB0\u0BCD\u0B95\u0BBE\u0BB2 \u0B87\u0BB2\u0BCD\u0BB2\u0BA4\u0BCD\u0BA4\u0BC8\u0B95\u0BCD \u0B95\u0BBE\u0BA3 \u0B95\u0BC0\u0BB4\u0BC7 \u0B89\u0BB3\u0BCD\u0BB3 \u0B8F\u0BA4\u0BC7\u0BA9\u0BC1\u0BAE\u0BCD \u0B92\u0BB0\u0BC1 \u0BAA\u0BBF\u0BB3\u0BBE\u0B9F\u0BCD\u0B9F\u0BC8\u0BA4\u0BCD \u0BA4\u0BC7\u0BB0\u0BCD\u0BA8\u0BCD\u0BA4\u0BC6\u0B9F\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD.'
            }
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {[
            { value: t.pricing, label: t.pricingLabel },
            { value: t.totalArea, label: t.totalLabel },
            { value: t.plotCount, label: t.plotLabel },
            { value: t.parkArea, label: t.parkLabel },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
              className="bg-[#F4F1EA] rounded-xl p-4 sm:p-5 border border-[#E5DFD3]">
              <p className="font-serif text-lg sm:text-xl font-bold text-[#2C4C3B]">{stat.value}</p>
              <p className="text-[#5C6A61] text-xs mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Layout Map */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-8">
          <button onClick={() => setShowMap(!showMap)} data-testid={LANDING.plotMap}
            className="w-full group relative overflow-hidden rounded-2xl border border-[#E5DFD3] bg-white">
            <div className={`transition-all duration-500 ${showMap ? 'max-h-[800px]' : 'max-h-[300px] sm:max-h-[400px]'} overflow-hidden`}>
              <img src={MEDIA.layoutMap} alt="Lakshmi Nivasa Plot Layout Map" className="w-full h-auto" loading="lazy" />
            </div>
            <div className={`absolute inset-x-0 bottom-0 flex items-end justify-center pb-4 transition-opacity ${showMap ? 'opacity-0' : 'opacity-100'}`}>
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
              <span className="relative flex items-center gap-2 bg-[#2C4C3B] text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-lg group-hover:-translate-y-0.5 transition-transform">
                <Expand className="w-4 h-4" />{t.viewMap}
              </span>
            </div>
          </button>
        </motion.div>

        {/* Check Availability Button */}
        <AnimatePresence>
          {!showGrid && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="text-center mb-8">
              <button onClick={() => setShowGrid(true)} data-testid="check-availability-btn"
                className="group inline-flex items-center gap-2.5 bg-[#2C4C3B] hover:bg-[#213A2D] text-white px-8 py-4 rounded-xl text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-[#2C4C3B]/15">
                <LayoutGrid className="w-4.5 h-4.5" />
                {t.checkAvailability}
                <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              </button>
              <p className="text-[#5C6A61] text-xs mt-3">
                {counts.available} {t.filterAvailable} &middot; {counts.launch} {t.filterLaunch} &middot; {counts.sold} {t.filterSold}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Plot Grid */}
        <AnimatePresence>
          {showGrid && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
              {/* Filters */}
              <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2" data-testid={LANDING.plotFilter}>
                <Filter className="w-4 h-4 text-[#5C6A61] flex-shrink-0" />
                {filters.map((f) => (
                  <button key={f.key} data-testid={`plot-filter-${f.key}`} onClick={() => setFilter(f.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                      filter === f.key
                        ? f.key === 'vaastu' ? 'bg-amber-500 text-white shadow-sm' : 'bg-[#2C4C3B] text-white shadow-sm'
                        : 'bg-[#F4F1EA] text-[#5C6A61] hover:bg-[#E5DFD3]'
                    }`}>
                    {f.key === 'vaastu' && <Compass className="w-3 h-3" />}
                    {f.label}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === f.key ? 'bg-white/20' : 'bg-[#E5DFD3]'}`}>{f.count}</span>
                  </button>
                ))}
              </div>

              {/* Grid */}
              <div data-testid={LANDING.plotTable} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                {filteredPlots.map((plot, i) => {
                  const sc = statusColors[plot.status];
                  const isVaastu = plot.vaastu && plot.status !== 'sold';
                  return (
                    <motion.button
                      key={plot.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: Math.min(i * 0.02, 0.3), duration: 0.3 }}
                      onClick={() => plot.status !== 'sold' && onPlotClick?.(plot)}
                      data-testid={`plot-card-${plot.id}`}
                      className={`relative text-left p-3 rounded-xl border transition-all duration-200 ${
                        plot.status === 'sold'
                          ? `${sc.border} ${sc.bg} opacity-60 cursor-not-allowed`
                          : isVaastu
                            ? 'border-amber-400 bg-amber-50/50 hover:shadow-lg hover:shadow-amber-200/50 hover:-translate-y-1 cursor-pointer ring-1 ring-amber-300'
                            : `${sc.border} ${sc.bg} hover:shadow-md hover:-translate-y-0.5 cursor-pointer`
                      }`}
                    >
                      {/* Vaastu glow indicator */}
                      {isVaastu && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center shadow-sm">
                          <Compass className="w-3 h-3 text-white" />
                        </div>
                      )}

                      <div className="flex items-start justify-between mb-2">
                        <span className="font-serif text-lg font-bold text-[#1A2520]">#{plot.id}</span>
                        <span className={`w-2 h-2 rounded-full ${sc.dot} mt-1.5`} />
                      </div>
                      <p className="text-sm font-semibold text-[#1A2520]">
                        {plot.area.toLocaleString()} <span className="text-xs font-normal text-[#5C6A61]">{t.sqft}</span>
                      </p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <MapPin className="w-3 h-3 text-[#5C6A61]" />
                        <span className="text-[11px] text-[#5C6A61]">{facingTranslations[lang]?.[plot.facing] || plot.facing}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5">
                        <Badge className={`text-[10px] px-2 py-0.5 ${sc.bg} ${sc.text} border ${sc.border} font-medium`} variant="outline">
                          {statusLabel(plot.status)}
                        </Badge>
                        {isVaastu && (
                          <Badge className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-300 font-medium" variant="outline">
                            {tv?.holdTag || 'Vaastu'}
                          </Badge>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              <div className="text-center mt-6">
                <button onClick={() => setShowGrid(false)} className="text-[#5C6A61] hover:text-[#1A2520] text-xs font-medium transition-colors">
                  {t.hideAvailability} &uarr;
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contact */}
        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-8 text-center">
          <a
            href={`https://wa.me/919403890991?text=${encodeURIComponent(lang === 'en' ? 'Hi, I would like to know the pricing details for Lakshmi Nivasa plots.' : 'வணக்கம், லக்ஷ்மி நிவாஸா பிளாட்டுகளின் விலை விவரங்களை அறிய விரும்புகிறேன்.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 bg-[#D48C57] hover:bg-[#C07A4A] text-white px-8 py-4 rounded-xl text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-[#D48C57]/15"
          >
            {t.contactPricing}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
